import { effect, inject, Injectable, OnDestroy, resource, signal } from '@angular/core';
import { API_BASE_URL } from '@app/API_BASE_URL';
import { HubConnection, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr';
import { RequestChangedNotification } from '@models/live-notifications/request-changed-notification.model';
import { OAuthService } from 'angular-oauth2-oidc';
import { Subject } from 'rxjs';

const NOTIFICATIONS_HUB_PATH = '/hubs/notifications';

interface GroupAction {
    method: 'Subscribe' | 'Unsubscribe';
    group: string;
}

// connects to Medpace.PCS.Api/Hubs/NotificationHub.cs; must be component-provided (not providedIn: 'root')
// so the connection lifecycle matches the page that needs it, per-page connection scope
@Injectable()
export class NotificationHubService implements OnDestroy {
    private readonly baseUrl = inject(API_BASE_URL);
    private readonly oauthService = inject(OAuthService);

    private hubConnection: HubConnection | undefined;
    private readonly joinedGroups = new Set<string>();
    private readonly requestChangedSubject = new Subject<RequestChangedNotification>();
    public readonly requestChanged$ = this.requestChangedSubject.asObservable();

    // starts connecting as soon as the service is instantiated - no explicit connect() call needed
    private readonly connectResource = resource({
        loader: async () => {
            if (this.hubConnection) return true;
            this.hubConnection = new HubConnectionBuilder()
                .withUrl(`${this.baseUrl}${NOTIFICATIONS_HUB_PATH}`, {
                    accessTokenFactory: () => this.oauthService.getAccessToken(),
                })
                .withAutomaticReconnect()
                .build();
            this.hubConnection.on('RequestChanged', (notification: RequestChangedNotification) =>
                this.requestChangedSubject.next(notification)
            );
            this.hubConnection.onreconnected(() => this.rejoinGroups());
            await this.hubConnection.start();
            // resource.hasValue() checks for a defined value, so the loader must resolve with one
            return true;
        },
    });

    ngOnDestroy(): void {
        this.hubConnection?.stop();
    }

    private async rejoinGroups(): Promise<void> {
        for (const group of this.joinedGroups) {
            await this.invokeIfConnected('Subscribe', group);
        }
    }

    private async invokeIfConnected(method: 'Subscribe' | 'Unsubscribe', group: string): Promise<void> {
        if (this.hubConnection?.state !== HubConnectionState.Connected) return;

        try {
            await this.hubConnection.invoke(method, group);
        } catch (error) {
            // server rejects subscriptions the caller isn't authorised for (HubException) - don't break the page
            console.error(`SignalR ${method} for group "${group}" failed`, error);
        }
    }

    private readonly pendingGroupActions = signal<readonly GroupAction[]>([]);
    private isDrainingGroupActions = false;

    // sole trigger for draining: fires whenever the connection becomes ready or a new action is queued
    private readonly drainGroupActionsEffect = effect(() => {
        if (this.connectResource.hasValue() && this.pendingGroupActions().length > 0) {
            this.drainGroupActionQueue();
        }
    });

    invokeJoinGroup(group: string): void {
        this.pendingGroupActions.update((actions) => [...actions, { method: 'Subscribe', group }]);
    }

    invokeLeaveGroup(group: string): void {
        this.pendingGroupActions.update((actions) => [...actions, { method: 'Unsubscribe', group }]);
    }

    private async drainGroupActionQueue(): Promise<void> {
        if (this.isDrainingGroupActions) return;
        this.isDrainingGroupActions = true;

        try {
            while (this.pendingGroupActions().length > 0) {
                const [next, ...rest] = this.pendingGroupActions();
                this.pendingGroupActions.set(rest);

                if (next.method === 'Subscribe') this.joinedGroups.add(next.group);
                else this.joinedGroups.delete(next.group);

                await this.invokeIfConnected(next.method, next.group);
            }
        } finally {
            this.isDrainingGroupActions = false;
        }
    }
}
